import React from 'react';
import styled from 'styled-components';
import queryString from 'query-string';
import deepEqual from 'fast-deep-equal';
import { parse as parseContentDispositionHeader } from 'content-disposition-header';
import {
  CLAIM_DEFAULT_INSURED_RISK_TYPE,
  DEFAULT_QUERY_PARAM_CLAIM_SORT,
  HIDDEN_OQIO_USER_EMAILS,
  SETTLEMENT_REGULATION_FIELD_KEYS
} from '../consts/consts';
import { INSURED_RISK_TYPES_DICTIONARY } from '../consts/dictionaries';
import { deviceSizeNumber } from '../components/ui/Layout/breakpoints';

const EmptyValueWrapper = styled.span`
  color: var(--greyscale-concrete);
  display: flex;
`;

export const currencyValueFormat = (value, currencyPosition = 'after') => {
  if (value === null || value === undefined || isNaN(value)) {
    return null;
  }
  const localization = currencyPosition === 'after' ? 'de-DE' : 'pt-BR';
  const returnValue = new Intl.NumberFormat(localization, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(value);

  return returnValue;
};

export const decimalValueFormat = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return null;
  }
  return new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  }).format(value);
};

export const inputNumberFormatterWithoutCurrency = (value) => {
  const result = value
    ? new Intl.NumberFormat('de-DE', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      }).format(value)
    : '';

  return result.toString();
};

export const inputSquaremeterFormatter = (value) => {
  const parsedValue = value ? value.toString().replace('.', ',') : '';
  const parseDecimalPlaces =
    parsedValue.indexOf(',') >= 0
      ? parsedValue.substr(0, parsedValue.indexOf(',')) + parsedValue.substr(parsedValue.indexOf(','), 3)
      : parsedValue;

  return `${parseDecimalPlaces}`;
};

export const squaremeterParser = (value) => {
  const parsedValue =
    value && value.indexOf(',') >= 0
      ? value.substr(0, value.indexOf(',')) + value.substr(value.indexOf(','), 3)
      : value;

  return parsedValue
    ? parsedValue
        .replace(/[^0-9,.]+/g, '')
        .replace(/(,.*?),(.*,)?/, '$1')
        .replace(/,+/g, '.')
    : '';
};

export const moneyParser = (value) => {
  // If the vale has decimal places, we keep only the first two
  const parsedValue =
    value && value.indexOf(',') >= 0
      ? value.substr(0, value.indexOf(',')) + value.substr(value.indexOf(','), 3)
      : value;

  const result = parsedValue
    ? parsedValue
        // Replaces "€ ", "€", or a series of dots with an empty string
        .replace(/€\s?|(\.*)/g, '')
        // Replace any character that is NOT a number or a comma with an empty string
        .replace(/[^0-9,]+/g, '')
        //? Old code (seems to remove multiple commas), not sure if it is needed
        .replace(/(,.*?),(.*,)?/, '$1')
        // Replaces comma with dot (as floats are represented with dots)
        .replace(/,+/g, '.')
    : '';

  return result;
};

export const removeEmpty = (item) => {
  const localItem = item;
  if (typeof localItem === 'object') {
    Object.keys(localItem).forEach((key) => {
      if (localItem[key] && typeof localItem[key] === 'object') removeEmpty(localItem[key]);
      else if (localItem[key] === undefined) delete localItem[key];
    });
  }
  return localItem;
};

/**
 * Searches for a `meta_data` description by property name within a given object.
 * If the property is found, it returns the value of the specified item key. If not, it returns the default value.
 *
 * @param {Object} object - The object containing the metadata.
 * @param {string} propertyName - The property name to search for within the metadata.
 * @param {*} defaultValue - The default value to return if the property is not found.
 * @param {string} itemKey - The key of the item within the metadata to retrieve the value from.
 * @returns {*} The found value associated with the itemKey or the defaultValue if not found.
 */
export const findMetaDataDescriptionByProperty = (object, propertyName, defaultValue, itemKey) => {
  const foundValue = object?.meta_data?.find((item) => item.rel === propertyName)?.[itemKey];
  return foundValue ?? defaultValue;
};

// returns the insured risk type of the insured risk
export const findInsuredRiskTypeByInsuredRiskId = (insuredRisks, insuredRiskId) => {
  const insuredRiskType =
    insuredRisks.find((insuredRisk) => insuredRisk.id === insuredRiskId)?.type || CLAIM_DEFAULT_INSURED_RISK_TYPE;
  return insuredRiskType;
};

// returns risk details from claim
export const getRiskDetails = (claim) => {
  const riskStreet = findMetaDataDescriptionByProperty(claim, 'risk_street', null, 'description');
  const riskZipcode = findMetaDataDescriptionByProperty(claim, 'risk_zipcode', null, 'description');
  const riskTown = findMetaDataDescriptionByProperty(claim, 'risk_town', null, 'description');
  const riskLatitude = findMetaDataDescriptionByProperty(claim, 'risk_latitude', null, 'value_double');
  const riskLongitude = findMetaDataDescriptionByProperty(claim, 'risk_longitude', null, 'value_double');
  const riskElevation = findMetaDataDescriptionByProperty(claim, 'risk_elevation', null, 'value_double');
  const riskHousingStock = findMetaDataDescriptionByProperty(claim, 'housing_stock', null, 'description');
  const riskText = claim?.risk_text || null;
  const riskId = claim?.risk_id || null;

  return {
    riskStreet,
    riskZipcode,
    riskTown,
    riskLatitude,
    riskLongitude,
    riskElevation,
    riskHousingStock,
    riskText,
    riskId
  };
};

// returns a boolean if the claim has coordinates
// All three fields use != null to correctly handle 0 as a valid value (e.g. elevation at sea level)
export const checkIfClaimHasCoordinates = (claim) => {
  const latitude = findMetaDataDescriptionByProperty(claim, 'risk_latitude', null, 'value_double');
  const longitude = findMetaDataDescriptionByProperty(claim, 'risk_longitude', null, 'value_double');
  const elevation = findMetaDataDescriptionByProperty(claim, 'risk_elevation', null, 'value_double');
  return latitude != null && longitude != null && elevation != null;
};

export const getSortParameterValue = (columnKey, sortOrder) => {
  if (!columnKey || !sortOrder) {
    return undefined;
  }
  const orderCharacter = sortOrder === 'ascend' ? '+' : '-';
  switch (columnKey) {
    case 'housingStockName':
      return `${orderCharacter}housing_stock`;
    case 'street':
      return `${orderCharacter}street`;
    case 'zipCode':
      return `${orderCharacter}zipcode`;
    case 'town':
      return `${orderCharacter}town`;
    case 'insuranceOrgId':
      return `${orderCharacter}insurance_org`;
    case 'insurancePolicyNumber':
      return `${orderCharacter}insurance_policy_number`;
    case 'addendum':
      return `${orderCharacter}addendum`;
    case 'startDate':
      return `${orderCharacter}start_date`;
    case 'endDate':
      return `${orderCharacter}end_date`;
    case 'customField':
      return `${orderCharacter}custom_field`;
    case 'companyCode':
      return `${orderCharacter}company_code`;
    case 'economicUnit':
      return `${orderCharacter}economic_unit`;
    case 'externalId':
      return `${orderCharacter}external_id`;
    default:
      return undefined;
  }
};

export const parseChartYAxisValue = (value) => `${decimalValueFormat(value)}€`;

const createActiveFilterListObject = (activeFilters) => {
  const activeFiltersObject = queryString.parse(activeFilters);
  const REMOVE_FROM_FILTERS = ['page', 'search', 'sort'];
  const activeFilterListFilters = Object.keys(activeFiltersObject).filter((key) => !REMOVE_FROM_FILTERS.includes(key));
  return activeFilterListFilters;
};

export const checkForActiveFilters = (activeFilters) => {
  const activeFilterListFilters = createActiveFilterListObject(activeFilters);
  const hasActiveFilters = Object.keys(activeFilterListFilters).length > 0;
  return hasActiveFilters;
};

export const getTableColumnsSortOrder = (sortingParameter, sortParameter) => {
  if (!sortingParameter) return null;

  const [sortParameterDirection, sortParameterWithoutDirection] = [
    sortingParameter.charAt(0),
    sortingParameter.slice(1)
  ];

  return sortParameterWithoutDirection === sortParameter
    ? sortParameterDirection === '+'
      ? 'ascend'
      : 'descend'
    : null;
};

// Cycles through ascend → descend → ascend again, skipping the default "no sort" state Ant Design uses.
export const SORT_DIRECTIONS_CYCLE = ['ascend', 'descend', 'ascend'];

export const sortStringDe = (valueA, valueB) => (valueA ?? '').localeCompare(valueB ?? '', 'de');

// ! Please keep into mind that it doesn't work for all cases. Specifically for the `insured-risk-to-policy` object type.
export const getTitleFromObject = (object, objectType) => {
  switch (objectType) {
    case 'risk':
      return `${object.street}, ${object.zipcode}, ${object.town}`;
    case 'policy':
      return `${INSURED_RISK_TYPES_DICTIONARY[object.type]}, ${object.insurance_policy_number} ${
        object.addendum ? `, ${object.addendum}` : ''
      }`;
    default:
      return object.title;
  }
};

// returns an array of translated insured risk types, sorted by the order of the dictionary
export const getTranslatedInsuredRiskTypes = (insuredRisksIds, insuredRisks) => {
  if (!insuredRisksIds || !insuredRisks) {
    return;
  }

  const filteredRisks = insuredRisks.filter((risk) => insuredRisksIds.includes(risk.id));

  const translatedTypes = filteredRisks
    .map((risk) => INSURED_RISK_TYPES_DICTIONARY[risk.type])
    .filter((type) => type !== undefined);

  const uniqueTranslatedTypes = [...new Set(translatedTypes)];
  const sortOrder = Object.values(INSURED_RISK_TYPES_DICTIONARY);
  uniqueTranslatedTypes.sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b));

  return uniqueTranslatedTypes;
};

// highlightMatches is a helper function that will highlight the search value in the options.
// also it will add a tooltip to the options that are known objects in the database.
export const highlightMatches = (text, searchValue, className = '', suffix = null) => {
  if (!searchValue.trim())
    return (
      <span className={className}>
        {text}
        {suffix}
      </span>
    );

  const regex = new RegExp(`(${searchValue})`, 'gi');
  const parts = text.split(regex);

  const highlightedText = parts.map((part, index) => {
    const key =
      part.toLowerCase() === searchValue.toLowerCase() ? `highlight-${index}-${part}` : `text-${index}-${part}`;

    return (
      <span key={key} className={part.toLowerCase() === searchValue.toLowerCase() ? 'highlight' : 'non-highlight'}>
        {part}
      </span>
    );
  });

  return (
    <span className={className}>
      {highlightedText}
      {suffix}
    </span>
  );
};

export const getFileNameFromApiResponseHeader = (responseHeader, fileExtension) => {
  const contentDispositionHeader = responseHeader['content-disposition'];
  let filename = parseContentDispositionHeader(contentDispositionHeader).parameters.filename;
  const defaultFilename = `default-filename${fileExtension}`;
  return filename || defaultFilename;
};

// returns only the images from the blobs (no pdf etc)
export const filterImagesFromBlobs = (blobs) => {
  if (blobs === undefined || blobs.length === 0) {
    return [];
  }

  return blobs.filter((blob) => {
    const fileType = blob.mime_type.split('/')[0];
    if (fileType === 'image') {
      return blob;
    }
    return false;
  });
};

export const EmptyValue = () => <EmptyValueWrapper>—</EmptyValueWrapper>;

// check if the device is mobile or tablet
export const isMobile = window.innerWidth <= deviceSizeNumber.mobile;
export const isTabletOrSmaller = window.innerWidth <= deviceSizeNumber.splitScreen;

// scroll function for list pages
// This function scrolls the last visited item (by data-id) into view
// If not found yet, it retries up to `maxRetries` times with a delay
export const scrollIntoView = (listRef, lastVisitedId, maxRetries = 10) => {
  if (!lastVisitedId || !listRef?.current) {
    return;
  }

  let retries = 0;

  const tryScroll = () => {
    const lastVisitedItem = listRef?.current?.querySelector(`[data-id="${lastVisitedId}"]`);
    const listItems = listRef?.current?.querySelector('.ant-list-items');

    if (lastVisitedItem) {
      lastVisitedItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } else if (retries < maxRetries) {
      retries++;
      setTimeout(tryScroll, 100);
    } else if (listItems && !isMobile) {
      listItems.scrollTo({ top: 0 });
    }
  };

  tryScroll();
};

// check if the claim status is open
export const isClaimStatusOpen = (claim) => claim?.status === 'open';

// get the current year
export const currentYear = new Date().getFullYear();

// check if the current month is December
export const isDecember = new Date().getMonth() === 11;

/* Helper Functions for the claim pdf summary */

// Converts a Blob into a File object with a specified name.
export const blobToFile = (blob, fileName) => {
  const file = new File([blob], fileName, { type: 'application/pdf' });
  file.lastModifiedDate = new Date();
  file.name = fileName;
  return file;
};

// Checks if the browser is Microsoft Edge.
export const isEdgeBrowser = navigator.userAgent.indexOf('Edge') > -1;

// Extracts a file from an API response, handling Edge/IE compatibility.
export const getFileFromApiResponse = (response) => {
  const fileName = getFileNameFromApiResponseHeader(response.headers, '.pdf');
  const blob = new Blob([response.data], { type: 'application/pdf' });

  if (isEdgeBrowser || window.navigator.msSaveOrOpenBlob) {
    return blobToFile(blob, fileName);
  }

  return new File([blob], fileName, { type: 'application/pdf' });
};

// Downloads a Blob as a file, supporting Edge/IE.
export const downloadBlobAsFile = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);

  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveBlob(blob, fileName);
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// Fetch a Blob as a File object, not for the user, but for internal use (reupload)
export const fetchBlobAsFile = async (blobMeta) => {
  if (!blobMeta?.url) throw new Error('Kein Download-Link für das Attachment vorhanden.');

  const res = await fetch(blobMeta.url, {
    method: 'GET'
  });

  if (!res.ok) throw new Error(`Download fehlgeschlagen (${res.status})`);

  const bin = await res.blob();

  const cd = res.headers.get('content-disposition');
  const nameFromCD = cd?.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1];
  const decodedCDName = nameFromCD ? decodeURIComponent(nameFromCD.replace(/"/g, '')) : null;
  const urlName = blobMeta.filename || decodedCDName || blobMeta.url.split('?')[0].split('/').pop() || 'attachment';

  const mime = blobMeta.mime_type || bin.type || 'application/octet-stream';

  try {
    return new File([bin], urlName, { type: mime });
  } catch {
    bin.name = urlName;
    return bin;
  }
};

/**
 * Checks if a value exists within an object (enum-like) or an array.
 * @param {any} value - The value to check.
 * @param {object|array} collection - An enum-like object or an array of valid values.
 * @returns {boolean} - Returns true if the value is valid, otherwise false.
 */
export const isValidEnumValue = (value, collection) => {
  if (Array.isArray(collection)) {
    return collection.includes(value); // ✅ Directly check if value exists in an array
  }
  if (typeof collection === 'object' && collection !== null) {
    return Object.values(collection).includes(value); // ✅ Check if value exists in object values
  }
  return false; // ✅ If neither object nor array, return false
};

export const isSettlementStatusCanceled = (status) => {
  return status === 'canceled';
};

export const isSettlementRegulated = (settlement) => {
  if (settlement.status === 'closed' || settlement.status === 'rejected') {
    return true;
  }

  return SETTLEMENT_REGULATION_FIELD_KEYS.some(
    (key) =>
      settlement[key] !== undefined &&
      settlement[key] !== null &&
      (!Array.isArray(settlement[key]) || settlement[key].length > 0)
  );
};

// Checks if a value is an array and an empty array
// Returns true if the value is an empty array, otherwise false
export const isEmptyArray = (array) => {
  if (!Array.isArray(array)) {
    return false;
  }
  return array.length === 0;
};

// Checks if the user is one of the internal oqio users that should be hidden
// from all user lists in the app (e.g. support user, connect-bot).
export const isHiddenOqioUser = (user) => {
  return HIDDEN_OQIO_USER_EMAILS.includes(user?.email?.toLowerCase());
};

// The following function is used to identify the changes between two objects (an initial one and an updated one).
// The function returns an object containing only the fields with the changes.
// This function is used on updating database objects, and it helps preventing concurrency problems where different users work on the same data simultaneously.
export const getChangedDataFields = (initialData, updatedData) => {
  const changedFields = {};

  Object.keys(updatedData).forEach((key) => {
    // The structure of meta_data needs special handling and, as it can not be changed directly by the frontend, we ignore it
    if (key === 'meta_data') {
      return;
    }

    const initialValue = initialData[key];
    const updatedValue = updatedData[key];

    if (Array.isArray(updatedValue)) {
      const changesForArrayField = getChangesForArrayField(initialValue, updatedValue);
      if (changesForArrayField) {
        changedFields[key] = changesForArrayField;
      }
    } else {
      // Forms sometines return empty strings when fields are empty, so we need to parse the values back to null
      let parsedUpdatedValue = typeof updatedValue === 'string' && updatedValue.length === 0 ? null : updatedValue;
      if (initialValue !== parsedUpdatedValue) {
        changedFields[key] = parsedUpdatedValue;
      }
    }
  });

  return changedFields;
};

const getChangesForArrayField = (initialArray, updatedArray) => {
  const itemsToBeAdded = [];
  const itemsToBeRemoved = [];
  const itemsToBeUpdated = [];

  updatedArray.forEach((item) => {
    const initialItemWithSameId = initialArray.find((initialItem) => initialItem.id === item.id);

    if (!initialItemWithSameId) {
      itemsToBeAdded.push(item);
    } else {
      const updatedFields = {};

      Object.keys(item).forEach((key) => {
        // The structure of meta_data needs special handling and, as it can not be changed directly by the frontend, we ignore it
        if (key === 'meta_data') {
          return;
        }

        const initialValue = initialItemWithSameId[key];
        const updatedValue = item[key];
        // Forms sometines return empty strings when fields are empty, so we need to parse the values back to null
        const parsedUpdatedValue = typeof updatedValue === 'string' && updatedValue.length === 0 ? null : updatedValue;
        const isValueArray = Array.isArray(parsedUpdatedValue);

        if (parsedUpdatedValue != initialValue || (isValueArray && !deepEqual(parsedUpdatedValue, initialValue))) {
          updatedFields[key] = parsedUpdatedValue;
        }
      });

      if (Object.keys(updatedFields).length > 0) {
        itemsToBeUpdated.push({
          id: item.id,
          ...updatedFields
        });
      }
    }
  });

  initialArray.forEach((item) => {
    const isItemRemoved = !updatedArray.some((updatedItem) => updatedItem.id === item.id);
    if (isItemRemoved) {
      itemsToBeRemoved.push(item);
    }
  });

  const areAnyChanges = itemsToBeAdded.length || itemsToBeRemoved.length || itemsToBeUpdated.length;

  return areAnyChanges ? { itemsToBeAdded, itemsToBeRemoved, itemsToBeUpdated } : null;
};

// Maps legacy claim sort values from old bookmarks to the current backend value.
const LEGACY_CLAIMS_SORT_MAP = {
  '-updated_at': DEFAULT_QUERY_PARAM_CLAIM_SORT,
  updated_at: 'updated_at_claim_and_settlements_and_messages'
};

export const normalizeClaimsSort = (sort) => LEGACY_CLAIMS_SORT_MAP[sort] || sort;
